import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  CanMatchFn,
  Data,
  Route,
  Router,
  RouterStateSnapshot,
  UrlSegment,
  UrlTree,
} from '@angular/router';

import { AuthService } from '../services/auth.service';

const buildForbiddenRedirect = (router: Router): UrlTree =>
  router.createUrlTree(['/forbidden']);

const buildLoginRedirect = (router: Router, returnUrl: string): UrlTree =>
  router.createUrlTree(['/login'], { queryParams: { returnUrl } });

const resolveAttemptedUrl = (segments: readonly UrlSegment[]): string => {
  const path = segments.map((segment) => segment.path).filter(Boolean).join('/');
  return path.length > 0 ? `/${path}` : '/';
};

const readRequiredRoles = (data?: Data): readonly string[] => {
  const roles = data?.['roles'];
  return Array.isArray(roles) ? roles : [];
};

const hasRequiredRole = (userRoles: readonly string[], requiredRoles: readonly string[]): boolean => {
  if (requiredRoles.length === 0) {
    return true;
  }

  return requiredRoles.some((role) => userRoles.includes(role));
};

const evaluateRoles = (
  authService: AuthService,
  router: Router,
  requiredRoles: readonly string[],
  returnUrl: string,
): boolean | UrlTree => {
  if (!authService.isLoggedIn()) {
    return buildLoginRedirect(router, returnUrl);
  }

  const user = authService.getCurrentUser();
  const userRoles = user?.roles ?? [];

  if (hasRequiredRole(userRoles, requiredRoles)) {
    return true;
  }

  return buildForbiddenRedirect(router);
};

export const roleGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
): boolean | UrlTree => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const requiredRoles = readRequiredRoles(route.data);

  return evaluateRoles(authService, router, requiredRoles, state.url);
};

export const roleMatchGuard: CanMatchFn = (
  route: Route,
  segments: UrlSegment[],
): boolean | UrlTree => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const requiredRoles = readRequiredRoles(route.data);

  return evaluateRoles(
    authService,
    router,
    requiredRoles,
    resolveAttemptedUrl(segments),
  );
};
